// src/components/quizzes/kit/Celebration.js
//
// The big "well done!" screens of a game — for students AND educators:
//
//   <RoundDone right={8} total={10} onGo={…} />
//        after the questions: stars, "8 out of 10!", then on to the bonus
//   <GameDone quizScore bonusScore right total onContinue>{coins…}</GameDone>
//        the end: their avatar cheering, stars, the scores, the coins earned
//
// Stars: 3 for 90%+, 2 for 70%+, 1 for finishing.

import React, { useEffect } from "react";
import MyAvatar from "../../avatar/MyAvatar";
import { launchConfetti } from "../../../utils/confetti";
import "./celebration.css";

export const starsFor = (right, total) => {
  if (!total) return 1;
  const p = right / total;
  return p >= 0.9 ? 3 : p >= 0.7 ? 2 : 1;
};

const HEADLINES = {
  3: ["Superstar! 🌟", "Amazing work!", "You nailed it!"],
  2: ["Great job! 🎉", "Way to go!", "Nice work!"],
  1: ["You did it! 💪", "Good effort!", "Keep going!"],
};

const Stars = ({ n }) => (
  <div className="qc-stars" aria-label={`${n} out of 3 stars`}>
    {[1, 2, 3].map((s) => (
      <span key={s} className={`qc-star ${s <= n ? "is-on" : ""}`} style={{ animationDelay: `${0.25 + s * 0.22}s` }}>
        ★
      </span>
    ))}
  </div>
);

const Burst = () => (
  <span className="qc-burst" aria-hidden="true">
    {Array.from({ length: 18 }, (_, i) => (
      <i key={i} style={{ "--a": `${i * 20}deg`, "--d": `${(i % 6) * 60}ms` }} />
    ))}
  </span>
);

export const RoundDone = ({ right, total, onGo, bonus = true }) => {
  const n = starsFor(right, total);
  useEffect(() => {
    if (n >= 2) launchConfetti();
    const t = setTimeout(() => onGo?.(), 6000);
    return () => clearTimeout(t);
  }, [n, onGo]);
  return (
    <div className="qk-scene qc-center">
      <div className="qc-card is-small">
        <Burst />
        <p className="qc-kicker">Questions done!</p>
        <h1 className="qc-title">{HEADLINES[n][right % 3]}</h1>
        <Stars n={n} />
        {total > 0 && (
          <p className="qc-big">
            <b>{right}</b> out of <b>{total}</b> right
          </p>
        )}
        <button type="button" className="qc-go" onClick={onGo} autoFocus>
          {bonus ? "💎 Bonus round!" : "See my score"}
        </button>
      </div>
    </div>
  );
};

export const GameDone = ({ quizScore = 0, bonusScore = 0, right, total, title = "", onContinue, children }) => {
  const n = starsFor(right ?? 1, total ?? 1);
  useEffect(() => {
    launchConfetti();
    const t = setTimeout(() => launchConfetti(), 900);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="qk-scene qc-center">
      <div className="qc-card">
        <Burst />
        <div className="qc-avatar">
          <MyAvatar size={130} />
        </div>
        <p className="qc-kicker">{title ? `${title} — complete!` : "Game complete!"}</p>
        <h1 className="qc-title">{HEADLINES[n][(quizScore + bonusScore) % 3]}</h1>
        <Stars n={n} />
        <div className="qc-scores">
          <div>
            <span>🎮 Game</span>
            <b>{quizScore}</b>
          </div>
          <div>
            <span>💎 Bonus</span>
            <b>{bonusScore}</b>
          </div>
          <div className="is-total">
            <span>🏆 Total</span>
            <b>{quizScore + bonusScore}</b>
          </div>
        </div>
        <p className="qc-note">You earned this topic's game star! ⭐</p>
        {children}
        <button type="button" className="qc-go" onClick={onContinue}>
          Continue ➜
        </button>
      </div>
    </div>
  );
};
